import type { IAccount } from "@/modules/account/common/types";
import type {
  IBudgetTransferTemplateForm,
  IBudgetTransferTemplateRequest
} from "@/modules/template/common/types";
import {
  useCreateBudgetTransferTemplateMutation,
  useGetBudgetTransferTemplateQuery,
  useUpdateBudgetTransferTemplateMutation
} from "@/modules/template/services";
import {
  BudgetTransferKeys,
  PartyType,
  ResidenceType,
  TransferKeys,
} from "@/modules/transaction/common/enum";
import {
  RegionCodeSelectOption
} from "@/modules/transaction/components/BudgetTransfer/RegionCodeSelectOption";
import {
  budgetTransferAdditionalFields,
  getDocumentTypeOptions,
  getPartyTypeOptions,
  getResidencyOptions,
} from "@/modules/transaction/helpers";
import { useBudgetTransfer } from "@/modules/transaction/hooks";
import { useLazyGetBudgetTransferCommissionQuery, } from "@/modules/transaction/services";
import { CURRENCY, INPUT_MAX_LIMIT } from "@/shared/common/constants";
import { SearchParamKeys } from "@/shared/common/enums";
import type { IExtendedObject } from "@/shared/common/types";
import { OwnAccountSelectOption, Select, TemplateLayout, } from "@/shared/components";
import { cleanupAmount, isValidAccount, isValidAmount } from "@/shared/helpers";
import { useHasFormChanges } from "@/shared/hooks";
import { RadioGroup } from "@ab.uitools/ui-library";
import { Checkbox } from "@ab.uitools/ui-library/components/Checkbox";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import type { SingleValue, TOption } from "@ab.uitools/ui-library/components/ReactSelect/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import uuid4 from "uuid4";

interface Props {
  isEdit?: boolean;
  isCopy?: boolean;
  accountsList: IAccount[];
  setIsTinValid: Dispatch<SetStateAction<boolean>>;
  setIsAccountNumberInvalid: Dispatch<SetStateAction<boolean>>;
}

export const BudgetTransferTemplateForm = ({
  isEdit,
  accountsList,
  setIsTinValid,
  setIsAccountNumberInvalid,
}: Props) => {
  const { t } = useTranslation();
  const { operationId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const idempotencyKey = useRef(uuid4()).current;
  const templateName = searchParams.get(SearchParamKeys.TemplateName);

  const [defaultData, setDefaultData] = useState<IExtendedObject>({});

  const [getCommission] = useLazyGetBudgetTransferCommissionQuery();
  const {
    data: budgetTransferTemplate,
    isLoading: isGetTemplateLoading,
  } = useGetBudgetTransferTemplateQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: !isEdit && !operationId
  });

  const [
    createBudgetTransferTemplateApi,
    { isLoading: isCreateLoading },
  ] = useCreateBudgetTransferTemplateMutation();
  const [
    updateBudgetTransferTemplateApi,
    { isLoading: isUpdateLoading },
  ] = useUpdateBudgetTransferTemplateMutation();

  const {
    watch,
    reset,
    values,
    errors = {},
    userTin,
    setValue,
    taxPayers,
    isTaxAccount,
    fieldChanges,
    budgetAccount,
    setBudgetAccount,
    canGetCommission,
    handleClickTarget,
    regionCodeOptions,
    prevCreditAccountRef,
    accountsFilteredList,
    getBudgetAccountInfo,
    handleChangePartType,
    handleGetRegionCodeInfo,
    handleClickAuthorizedTransfer,
  } = useBudgetTransfer({
    setIsTinValid,
    accountsList,
    setIsAccountNumberInvalid,
  });

  const setDefaultValues = () => {
    const data = {
      creditBudgetAccount: budgetTransferTemplate?.creditBudgetAccount,
      debitAccount: budgetTransferTemplate?.debitAccount?.number,
      isAuthorizedTransfer: !!budgetTransferTemplate?.isAuthorizedTransfer,
      authorizedBudgetTransferParty: budgetTransferTemplate?.isAuthorizedTransfer ? {
        fullName: budgetTransferTemplate?.authorizedBudgetTransferParty?.fullName,
        residency: budgetTransferTemplate?.authorizedBudgetTransferParty?.residency ?? ResidenceType.Resident,
        partyType: budgetTransferTemplate?.authorizedBudgetTransferParty?.partyType,
        tin: budgetTransferTemplate?.authorizedBudgetTransferParty?.tin,
        ssn: budgetTransferTemplate?.authorizedBudgetTransferParty?.ssn,
        documentType: budgetTransferTemplate?.authorizedBudgetTransferParty?.documentType,
        documentNumber: budgetTransferTemplate?.authorizedBudgetTransferParty?.documentNumber,
        address: budgetTransferTemplate?.authorizedBudgetTransferParty?.address,
        additionalInfo: budgetTransferTemplate?.authorizedBudgetTransferParty?.additionalInfo,
        authorizedPerson: budgetTransferTemplate?.authorizedBudgetTransferParty?.authorizedPerson,
      } : null,
      beneficiary: budgetTransferTemplate?.beneficiary,
      regionCode: budgetTransferTemplate?.regionCode,
      amount: budgetTransferTemplate?.amount,
      tin: budgetTransferTemplate?.tin,
      purpose: budgetTransferTemplate?.purpose,
      currency: budgetTransferTemplate?.currency,
      commissionAccount: budgetTransferTemplate?.commissionAccount?.number,
      messages: {
        reportCode: budgetTransferTemplate?.messages?.reportCode || "",
        message1: budgetTransferTemplate?.messages?.message1 || "",
        message2: budgetTransferTemplate?.messages?.message2 || "",
        message3: budgetTransferTemplate?.messages?.message3 || "",
        message4: budgetTransferTemplate?.messages?.message4 || "",
        message5: budgetTransferTemplate?.messages?.message5 || "",
        message6: budgetTransferTemplate?.messages?.message6 || "",
      }
    };
    reset?.(data);
    setDefaultData(data);
  };

  const currentData = useMemo(() => ({
    creditBudgetAccount: values.creditBudgetAccount,
    debitAccount: values.debitAccount,
    isAuthorizedTransfer: !!values?.isAuthorizedTransfer,
    authorizedBudgetTransferParty: values.isAuthorizedTransfer ? {
      fullName: values?.authorizedBudgetTransferParty?.fullName,
      residency: values?.authorizedBudgetTransferParty?.residency,
      partyType: values?.authorizedBudgetTransferParty?.partyType,
      tin: values?.authorizedBudgetTransferParty?.tin,
      ssn: values?.authorizedBudgetTransferParty?.ssn,
      documentType: values?.authorizedBudgetTransferParty?.documentType,
      documentNumber: values?.authorizedBudgetTransferParty?.documentNumber,
      address: values?.authorizedBudgetTransferParty?.address,
      additionalInfo: values?.authorizedBudgetTransferParty?.additionalInfo,
      authorizedPerson: values?.authorizedBudgetTransferParty?.authorizedPerson,
    } : null,
    beneficiary: values.beneficiary,
    amount: values.amount,
    tin: values.tin,
    purpose: values.purpose,
    currency: values.currency,
    commissionAccount: values.commissionAccount,
    regionCode: values?.regionCode,
    messages: {
      reportCode: values?.messages?.reportCode,
      message1: values?.messages?.message1,
      message2: values?.messages?.message2,
      message3: values?.messages?.message3,
      message4: values?.messages?.message4,
      message5: values?.messages?.message5,
      message6: values?.messages?.message6,
    }
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  const budgetTransferTemplateAction = useCallback((values: IBudgetTransferTemplateForm) => {
    const decimals = values.currency === CURRENCY.AMD.label ? 1 : 2;
    const requestData: IBudgetTransferTemplateRequest = {
      ...values,
      name: templateName ?? "",
      amount: +(+cleanupAmount(`${values.amount}`)).toFixed(decimals) || null,
      authorizedBudgetTransferParty: values.isAuthorizedTransfer ? values.authorizedBudgetTransferParty : null,
      debitAccount: accountsList?.find(({ number }) => number === values.debitAccount),
      commissionAccount: values.commissionAccount ? accountsList?.find(({ number }) => {
        return number === values.commissionAccount;
      }) : undefined,
    };
    return isEdit ?
      updateBudgetTransferTemplateApi({
        data: requestData,
        id: `${operationId}`
      }).unwrap() :
      createBudgetTransferTemplateApi({
        data: requestData,
        idempotencyKey,
      }).unwrap();
  }, [accountsList, budgetTransferTemplate]);

  const partyType = watch?.(BudgetTransferKeys.PartyType);
  const isAuthorizedTransfer = watch?.(BudgetTransferKeys.IsAuthorizedTransfer);

  useEffect(() => {
    if (isEdit && !!budgetTransferTemplate?.transactionType) {
      setDefaultValues();
      getBudgetAccountInfo(budgetTransferTemplate?.creditBudgetAccount);
    }
  }, [isEdit, budgetTransferTemplate]);

  useEffect(() => {
    if (!isEdit) {
      setValue(TransferKeys.Purpose, t("transfer.budgetTransfer.purpose"));
    }
  }, [isEdit]);

  return (
    <TemplateLayout
      title={t("template.budgetTransfer.title")}
      page="Budget transfer template"
      accountsList={accountsList}
      isEdit={isEdit}
      getCommission={getCommission}
      hasChanges={hasChanges}
      canGetCommission={canGetCommission}
      fieldChanges={fieldChanges}
      isLoading={isCreateLoading || isUpdateLoading}
      isGetTemplateLoading={isGetTemplateLoading}
      saveTemplate={budgetTransferTemplateAction}
    >
      <CollapseGroup
        className="collapse-group"
        items={[
          {
            content: <>
              <FormField
                name="creditBudgetAccount"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    type="numeric"
                    allowNegative={false}
                    allowLeadingZeros={false}
                    handleChange={(_, value) => {
                      if (value !== prevCreditAccountRef.current && isValidAccount(value)) {
                        getBudgetAccountInfo(value);
                      } else {
                        getBudgetAccountInfo.cancel();
                        setBudgetAccount(null);
                        setValue?.(BudgetTransferKeys.RegionCode, "");
                        prevCreditAccountRef.current = "";
                      }
                    }}
                    label={t("transfer.receiverAccount")}
                    placeholder={t("transfer.enterReceiverAccount")}
                  />
                )}
              />
              {budgetAccount?.description && (
                <div className="mt-20">
                  <Text weight="semibold">
                    {t("transfer.beneficiaryName")}
                  </Text>
                  <Text className="mb-32 mt-8">
                    {budgetAccount.description}
                  </Text>
                </div>
              )}
              <FormField
                name="isAuthorizedTransfer"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Checkbox
                    {...props}
                    onClick={handleClickAuthorizedTransfer}
                    label={t("transfer.budget.makeTransferForAnother")}
                  />
                )}
              />
              {!isAuthorizedTransfer && (
                <>
                  {isTaxAccount && values.regionCode && (
                    <div className="mt-20">
                      <Text weight="semibold">{t("transfer.regionalCode")}</Text>
                      <Text className="mb-32 mt-8">
                        {taxPayers?.regionCode} {taxPayers?.regionName}
                      </Text>
                    </div>
                  )}
                  {!!regionCodeOptions.length &&
                    values.creditBudgetAccount &&
                    !isTaxAccount && (
                      <FormField
                        name="regionCode"
                        className="mb-32"
                        shouldValidate
                        errorMessageIcon="infoFilled"
                        As={(props) => (
                          <Select
                            {...props}
                            options={regionCodeOptions}
                            label={t("transfer.regionalCode")}
                            placeholder={t("transfer.chooseReginalCode")}
                            selectedValue={values.regionCode}
                            components={{ Option: RegionCodeSelectOption }}
                          />
                        )}
                      />
                    )}
                </>
              )}
              {isAuthorizedTransfer && (
                <>
                  <FormField
                    name="authorizedBudgetTransferParty.fullName"
                    className="mb-32 mt-16"
                    errorMessageIcon="infoFilled"
                    As={(props) => (
                      <Input
                        {...props}
                        label={t("transfer.budgetTransfer.fullName")}
                        placeholder={t("transfer.budgetTransfer.enterFullName")}
                      />
                    )}
                  />
                  <FormField
                    name="authorizedBudgetTransferParty.residency"
                    className="mb-32 mt-16"
                    errorMessageIcon="infoFilled"
                    As={(props) => (
                      <RadioGroup
                        {...props}
                        label={t("transfer.budgetTransfer.thirdPartyResidence")}
                        selected={values.authorizedBudgetTransferParty?.residency}
                        options={getResidencyOptions({ t })}
                      />
                    )}
                  />
                  <FormField
                    name="authorizedBudgetTransferParty.partyType"
                    className="mb-32"
                    errorMessageIcon="infoFilled"
                    shouldValidate
                    As={(props) => (
                      <Select
                        {...props}
                        options={getPartyTypeOptions({ t })}
                        handleChange={(value) => handleChangePartType(value as SingleValue<TOption>)}
                        label={t("transfer.budgetTransfer.legalStatus")}
                        placeholder={t("transfer.budgetTransfer.chooseLegalStatus")}
                      />
                    )}
                  />
                  {partyType === PartyType.NaturalPerson && (
                    <FormField
                      name="authorizedBudgetTransferParty.ssn"
                      className="mb-32"
                      key="authorizedBudgetTransferParty.ssn"
                      errorMessageIcon="infoFilled"
                      As={(props) => (
                        <Input
                          {...props}
                          label={t("transfer.ssn")}
                          placeholder={t("transfer.budgetTransfer.ssn")}
                        />
                      )}
                    />
                  )}
                  {(partyType === PartyType.LegalEntity
                    || partyType === PartyType.PrivateEntrepreneur) && (
                    <FormField
                      name="authorizedBudgetTransferParty.tin"
                      className="mb-32"
                      key="authorizedBudgetTransferParty.tin"
                      errorMessageIcon="infoFilled"
                      As={(props) => (
                        <Input
                          {...props}
                          onChange={handleGetRegionCodeInfo}
                          label={t("transfer.tinCode")}
                          placeholder={t("transfer.budgetTransfer.enterTinCode")}
                        />
                      )}
                    />
                  )}
                  <FormField
                    name="authorizedBudgetTransferParty.documentType"
                    className="mb-32"
                    shouldValidate
                    errorMessageIcon="infoFilled"
                    As={(props) => (
                      <Select
                        {...props}
                        isClearable
                        setSelectedValue={(value) => {
                          if (!value) {
                            setValue(BudgetTransferKeys.DocumentType, "");
                            setValue(BudgetTransferKeys.DocumentNumber, "");
                          }
                        }}
                        options={getDocumentTypeOptions({ t })}
                        label={t("transfer.budgetTransfer.passportType")}
                        placeholder={t("transfer.budgetTransfer.choosePassportType")}
                      />
                    )}
                  />
                  {values?.authorizedBudgetTransferParty?.documentType && (
                    <FormField
                      name="authorizedBudgetTransferParty.documentNumber"
                      className="mb-32"
                      errorMessageIcon="infoFilled"
                      As={(props) => (
                        <Input
                          {...props}
                          label={t("transfer.budgetTransfer.documentData")}
                          placeholder={t("transfer.budgetTransfer.enterDocumentData")}
                        />
                      )}
                    />
                  )}
                  <FormField
                    name="authorizedBudgetTransferParty.address"
                    className="mb-32"
                    errorMessageIcon="infoFilled"
                    As={(props) => (
                      <Input
                        {...props}
                        label={t("transfer.budgetTransfer.address")}
                        placeholder={t("transfer.budgetTransfer.enterAddress")}
                      />
                    )}
                  />
                  <FormField
                    name="authorizedBudgetTransferParty.authorizedPerson"
                    className="mb-32"
                    errorMessageIcon="infoFilled"
                    As={(props) => (
                      <Input
                        {...props}
                        label={t("transfer.budgetTransfer.authorizedPerson")}
                        placeholder={t("transfer.budgetTransfer.enterAuthorizedPerson")}
                      />
                    )}
                  />
                  <FormField
                    name="authorizedBudgetTransferParty.additionalInfo"
                    className="mb-32"
                    errorMessageIcon="infoFilled"
                    As={(props) => (
                      <Input
                        {...props}
                        label={t("transfer.budgetTransfer.otherInformation")}
                        placeholder={t("transfer.budgetTransfer.enterOtherInformation")}
                      />
                    )}
                  />
                </>
              )}
              {isAuthorizedTransfer && (
                <>
                  {isTaxAccount && values.regionCode && (
                    partyType === PartyType.LegalEntity ||
                    partyType === PartyType.PrivateEntrepreneur
                  ) && (
                    <div className="mt-20">
                      <Text weight="semibold">{t("transfer.regionalCode")}</Text>
                      <Text className="mb-32 mt-8">
                        {taxPayers?.regionCode} {taxPayers?.regionName}
                      </Text>
                    </div>
                  )}
                  {!!partyType &&
                    !!regionCodeOptions.length &&
                    values.creditBudgetAccount &&
                    ((isTaxAccount && partyType === PartyType.NaturalPerson) || !isTaxAccount) && (
                      <FormField
                        name="regionCode"
                        className="mb-32"
                        shouldValidate
                        errorMessageIcon="infoFilled"
                        As={(props) => (
                          <Select
                            {...props}
                            isClearable
                            isCreatable
                            isSearchable
                            isCreateOnOutsideClick
                            options={regionCodeOptions}
                            label={t("transfer.regionalCode")}
                            placeholder={t("transfer.chooseReginalCode")}
                            selectedValue={values.regionCode}
                            components={{ Option: RegionCodeSelectOption }}
                          />
                        )}
                      />
                    )}
                </>
              )}
              <FormField
                name="amount"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    type="numeric"
                    allowNegative={false}
                    allowLeadingZeros={false}
                    label={t("general.amount")}
                    placeholder={t("general.enterAmount")}
                    thousandSeparator=","
                    decimalScale={values.currency === CURRENCY.AMD.label ? 1 : 2}
                    rightIconProps={{
                      Component: CURRENCY[values.currency]?.symbolIcon || "",
                    }}
                    isAllowed={({ formattedValue }) => isValidAmount(formattedValue)}
                  />
                )}
              />
              <FormField
                name="debitAccount"
                className="mb-32"
                shouldValidate
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Select
                    {...props}
                    isSearchable
                    className="custom-select"
                    options={accountsFilteredList || []}
                    label={t("transfer.debitAccount")}
                    placeholder={t("transfer.chooseDebitAccount")}
                    handleChange={(value) => handleClickTarget(value as SingleValue<TOption>)}
                    components={{ Option: OwnAccountSelectOption }}
                  />
                )}
              />
              <FormField
                name="purpose"
                className="mb-32"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    error={`${errors?.purpose?.message || ""}`}
                    label={t("general.purpose")}
                    placeHolder={t("general.enterYourPurpose")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
            </>,
            title: t("transfer.transferDetails"),
            value: "transfer_details",
            isOpen: true,
          },
          {
            content: <>
              <div className="mt-20">
                <Text weight="semibold">{t("transfer.tin")}</Text>
                <Text className="mb-32 mt-8">
                  {userTin}
                </Text>
              </div>
              {budgetTransferAdditionalFields.map((field) => (
                <FormField
                  key={field.name}
                  name={field.name}
                  className={field.classNames}
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Input
                      {...props}
                      label={t(field.label)}
                      placeholder={t(field.placeholder)}
                    />
                  )}
                />
              ))}
            </>,
            title: t("general.otherDetails"),
            value: "other_details",
            isOpen: true,
          },
        ]}/>
    </TemplateLayout>
  );
};
